import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, User, Calendar, Clock, X, ExternalLink, MessageSquare, Zap, Headset } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { analyzeUserMessage, getResponseForUserMessage, solutionsDatabase } from '@/utils/aiChatService';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const businessChallenges = [{
  id: 1,
  text: "I need more sales"
}, {
  id: 2,
  text: "My customer support is slow"
}, {
  id: 3,
  text: "I need better team collaboration"
}, {
  id: 4,
  text: "Managing bookings is a hassle"
}, {
  id: 5,
  text: "I'm an artist looking for solutions"
}, {
  id: 6,
  text: "I don't know what I need"
}];

interface Message {
  id: string;
  content: string | React.ReactNode;
  sender: 'user' | 'bot';
  options?: Array<{
    id: number;
    text: string;
  }>;
  solutions?: Array<{
    id: number;
    title: string;
    description: string;
    features?: string[];
    icon?: string;
  }>;
  showCTA?: boolean;
}
type ConversationState = 'greeting' | 'challenge' | 'solution' | 'details' | 'cta';
interface AIConsultantChatProps {
  fullPage?: boolean;
  isWidget?: boolean;
}

const AIConsultantChat: React.FC<AIConsultantChatProps> = ({
  fullPage = false,
  isWidget = false
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationState, setConversationState] = useState<ConversationState>('greeting');
  const [selectedChallenge, setSelectedChallenge] = useState<string | null>(null);
  const [selectedSolution, setSelectedSolution] = useState<{
    title: string;
    description: string;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    startConversation();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);

  const startConversation = () => {
    setIsTyping(true);
    setTimeout(() => {
      const greetingMessage: Message = {
        id: Date.now().toString(),
        content: <div>
            <p>Hey there! Struggling with a business challenge? I can help you figure out the best AI-powered app solution for your needs.</p>
            <p className="mt-2">Just type your challenge or select an option below:</p>
          </div>,
        sender: 'bot',
        options: businessChallenges
      };
      setMessages([greetingMessage]);
      setIsTyping(false);
      setConversationState('challenge');
    }, 1000);
  };

  const handleSendMessage = (text?: string) => {
    const messageText = text || inputValue;
    if (!messageText.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: messageText,
      sender: 'user'
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    switch (conversationState) {
      case 'challenge':
        handleChallengeInput(messageText);
        break;
      case 'solution':
        handleSolutionSelection(messageText);
        break;
      case 'details':
      case 'cta':
        handleFollowUpMessage(messageText);
        break;
      default:
        handleDefaultResponse(messageText);
    }
  };

  const handleChallengeInput = (challengeText: string) => {
    setSelectedChallenge(challengeText);

    const matchedChallenge = businessChallenges.find(c => c.text.toLowerCase() === challengeText.toLowerCase());
    if (matchedChallenge) {
      handlePredefinedChallenge(challengeText);
    } else {
      handleCustomUserInput(challengeText);
    }
  };

  const handlePredefinedChallenge = (challengeText: string) => {
    const categoryMap: {
      [key: string]: string;
    } = {
      "I need more sales": "sales",
      "My customer support is slow": "customer_support",
      "I need better team collaboration": "team_collaboration",
      "Managing bookings is a hassle": "booking",
      "I'm an artist looking for solutions": "creative",
      "I don't know what I need": "consultation"
    };
    const category = categoryMap[challengeText] || "consultation";
    const solutions = solutionsDatabase[category] || [];
    setTimeout(() => {
      const botResponse: Message = {
        id: Date.now().toString(),
        content: <div>
            <p>Got it! Based on your need to <strong>{challengeText.toLowerCase()}</strong>, you might benefit from:</p>
            <div className="mt-2 space-y-1">
              {solutions.map(solution => <p key={solution.id}>• {solution.title}</p>)}
            </div>
            <p className="mt-2">Which one interests you most?</p>
          </div>,
        sender: 'bot',
        solutions: solutions
      };
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
      setConversationState('solution');
    }, 1500);
  };

  const handleCustomUserInput = (userInput: string) => {
    const {
      response,
      categories,
      solutions
    } = getResponseForUserMessage(userInput);

    console.log("Analyzed message:", userInput);
    console.log("Detected categories:", categories);
    console.log("Primary category:", categories[0]);
    console.log("Solutions:", solutions);
    setTimeout(() => {
      const botResponse: Message = {
        id: Date.now().toString(),
        content: <div>
            <p>{response}</p>
            <div className="mt-2 space-y-1">
              {solutions.map(solution => <p key={solution.id}>• {solution.title}</p>)}
            </div>
            <p className="mt-2">Which one interests you most?</p>
          </div>,
        sender: 'bot',
        solutions: solutions
      };
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
      setConversationState('solution');
    }, 1500);
  };

  const handleSolutionSelection = (solutionTitle: string) => {
    let solution = null;
    for (const category in solutionsDatabase) {
      const found = solutionsDatabase[category].find(s => s.title.toLowerCase() === solutionTitle.toLowerCase() || s.title.toLowerCase().includes(solutionTitle.toLowerCase()));
      if (found) {
        solution = found;
        break;
      }
    }
    if (solution) {
      setSelectedSolution(solution);
    }
    setTimeout(() => {
      const botResponse: Message = {
        id: Date.now().toString(),
        content: <div>
            <p>Perfect! {solution ? `A ${solution.title.toLowerCase()} would be an excellent choice. Here's what it can do for you:` : 'A custom solution would be perfect for your needs.'}</p>
            
            {solution && solution.features ? <ul className="mt-2 space-y-1">
                {solution.features.map((feature, idx) => <li key={idx} className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>{feature}</span>
                  </li>)}
              </ul> : <ul className="mt-2 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Addresses your specific business needs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Integrates with your existing systems</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Provides measurable ROI within months</span>
                </li>
              </ul>}
            
            <p className="mt-3">What would you like to do next?</p>
          </div>,
        sender: 'bot',
        showCTA: true
      };
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
      setConversationState('cta');
    }, 1500);
  };

  const handleFollowUpMessage = (message: string) => {
    const {
      categories,
      solutions
    } = getResponseForUserMessage(message);
    const primaryCategory = categories[0];
    if (primaryCategory && primaryCategory !== 'consultation') {
      setTimeout(() => {
        const botResponse: Message = {
          id: Date.now().toString(),
          content: <div>
              <p>I see you're now interested in {primaryCategory.replace('_', ' ')} solutions. Here are some options that might help:</p>
              <div className="mt-2 space-y-1">
                {solutions.map(solution => <p key={solution.id}>• {solution.title}</p>)}
              </div>
              <p className="mt-2">Which one interests you most?</p>
            </div>,
          sender: 'bot',
          solutions: solutions
        };
        setMessages(prev => [...prev, botResponse]);
        setIsTyping(false);
        setConversationState('solution');
      }, 1500);
    } else {
      setTimeout(() => {
        const botResponse: Message = {
          id: Date.now().toString(),
          content: <div>
              <p>Thanks for sharing that additional information! Our experts would love to discuss your specific requirements in more detail and provide a customized solution.</p>
              <p className="mt-2">Would you like to:</p>
            </div>,
          sender: 'bot',
          showCTA: true
        };
        setMessages(prev => [...prev, botResponse]);
        setIsTyping(false);
      }, 1500);
    }
  };

  const handleDefaultResponse = (message: string) => {
    const {
      response,
      solutions
    } = getResponseForUserMessage(message);
    setTimeout(() => {
      const botResponse: Message = {
        id: Date.now().toString(),
        content: <div>
            <p>{response}</p>
            <div className="mt-2 space-y-1">
              {solutions.map(solution => <p key={solution.id}>• {solution.title}</p>)}
            </div>
            <p className="mt-2">Which one interests you most?</p>
          </div>,
        sender: 'bot',
        solutions: solutions
      };
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
      setConversationState('solution');
    }, 1500);
  };

  const handleOptionClick = (text: string) => {
    handleSendMessage(text);
  };

  const handleSolutionClick = (title: string) => {
    handleSendMessage(title);
  };

  const handleCTAClick = (action: 'portfolio' | 'booking' | 'quote') => {
    let userAction = '';
    switch (action) {
      case 'portfolio':
        userAction = "I'd like to see similar projects";
        break;
      case 'booking':
        userAction = "I want to book a free consultation";
        break;
      case 'quote':
        userAction = "I'd like to get a quick quote";
        break;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      content: userAction,
      sender: 'user'
    };
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    setTimeout(() => {
      let responseContent = '';
      switch (action) {
        case 'portfolio':
          responseContent = "Great! I'll redirect you to our portfolio page where you can see similar projects we've worked on.";
          setTimeout(() => window.location.href = '/services/portfolio', 2000);
          break;
        case 'booking':
          responseContent = "Perfect! I'll take you to our booking page where you can schedule a free consultation with our experts.";
          setTimeout(() => window.location.href = '/booking', 2000);
          break;
        case 'quote':
          responseContent = "Sure thing! Let me transfer you to get a quick estimate for your project.";
          toast.success("We've saved your consultation information. Our team will contact you within 24 hours with a quote.");
          setTimeout(() => window.location.href = '/booking', 2000);
          break;
      }
      const botResponse: Message = {
        id: Date.now().toString(),
        content: responseContent,
        sender: 'bot'
      };
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000);
  };

  const resetConversation = () => {
    setMessages([]);
    setSelectedChallenge(null);
    setSelectedSolution(null);
    setConversationState('greeting');
    startConversation();
  };

  const renderSolutionButton = (solution: any) => {
    const icon = solution.icon ? solution.icon : 'zap';
    return (
      <Button 
        key={solution.id} 
        variant="outline" 
        size="sm" 
        className="text-xs md:text-sm w-full justify-start text-left py-2.5 h-auto" 
        onClick={() => handleSolutionClick(solution.title)}
      >
        {icon && <span className="mr-2">
          {icon === 'zap' && <Zap size={14} />}
        </span>}
        {solution.title}
      </Button>
    );
  };

  return <div className={cn("flex flex-col bg-card rounded-lg overflow-hidden border border-border", fullPage ? "h-[600px]" : isWidget ? "h-[450px] w-[350px]" : "h-full")}>
      <div className="p-4 bg-primary/5 border-b border-border flex justify-between items-center">
        <div className="flex items-center">
          <Avatar variant="glow" className="mr-3 h-10 w-10">
            <AvatarFallback glowEffect={true} icon={true} />
          </Avatar>
          <div>
            <h3 className="font-medium text-sm md:text-base">LovaBuild AI Consultant</h3>
            <p className="text-xs text-muted-foreground">Online</p>
          </div>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-6">
        {messages.map(message => <div key={message.id} className={cn("flex", message.sender === 'user' ? "justify-end" : "justify-start")}>
            <div className={cn("max-w-[85%] rounded-lg p-3 md:p-4", message.sender === 'user' ? "bg-primary text-primary-foreground" : "bg-muted")}>
              {message.sender === 'bot' && (
                <div className="flex items-start mb-2">
                  <Avatar variant="brand" className="h-7 w-7 mr-2 flex-shrink-0 mt-0.5">
                    <AvatarFallback className="bg-luxury-900 text-gold" icon={true} />
                  </Avatar>
                  <span className="text-xs font-medium text-muted-foreground">LovaBuild Assistant</span>
                </div>
              )}
              
              {typeof message.content === 'string' ? (
                <p className="text-sm md:text-base leading-relaxed">{message.content}</p>
              ) : message.content}
              
              {message.options && <div className="mt-4 flex flex-wrap gap-2">
                  {message.options.map(option => <Button key={option.id} variant="outline" size="sm" className="text-xs md:text-sm px-3 py-1.5 h-auto" onClick={() => handleOptionClick(option.text)}>
                      {option.text}
                    </Button>)}
                </div>}
              
              {message.solutions && <div className="mt-4 space-y-2.5">
                  {message.solutions.map(solution => renderSolutionButton(solution))}
                </div>}
              
              {message.showCTA && <div className="mt-4 space-y-2.5">
                  <Button variant="outline" size="sm" className="w-full flex items-center justify-start text-left text-xs md:text-sm py-2.5 h-auto" onClick={() => handleCTAClick('portfolio')}>
                    <ExternalLink size={14} className="mr-2" />
                    See Similar Projects
                  </Button>
                  <Button variant="outline" size="sm" className="w-full flex items-center justify-start text-left text-xs md:text-sm py-2.5 h-auto" onClick={() => handleCTAClick('booking')}>
                    <Calendar size={14} className="mr-2" />
                    Book a Free Consultation
                  </Button>
                  <Button variant="outline" size="sm" className="w-full flex items-center justify-start text-left text-xs md:text-sm py-2.5 h-auto" onClick={() => handleCTAClick('quote')}>
                    <Clock size={14} className="mr-2" />
                    Get a Quick Quote
                  </Button>
                </div>}
            </div>
          </div>)}
        
        {isTyping && <div className="flex justify-start">
            <div className="max-w-[85%] rounded-lg p-4 bg-muted">
              <div className="flex items-start mb-2">
                <Avatar variant="brand" className="h-7 w-7 mr-2 flex-shrink-0 mt-0.5">
                  <AvatarFallback className="bg-luxury-900 text-gold" icon={true} />
                </Avatar>
                <div className="flex gap-1.5 items-center">
                  <div className="w-2 h-2 rounded-full bg-gold/40 animate-pulse"></div>
                  <div className="w-2 h-2 rounded-full bg-gold/40 animate-pulse delay-75"></div>
                  <div className="w-2 h-2 rounded-full bg-gold/40 animate-pulse delay-150"></div>
                </div>
              </div>
            </div>
          </div>}
        
        <div ref={messagesEndRef} />
      </div>
      
      <form onSubmit={e => {
      e.preventDefault();
      handleSendMessage();
    }} className="border-t p-3 md:p-4 flex gap-2 items-center">
        <Button type="button" variant="ghost" size="icon" className="rounded-full" onClick={resetConversation}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-refresh-ccw"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" /><path d="M16 21h5v-5" /></svg>
        </Button>
        <Input placeholder="Type your message..." value={inputValue} onChange={e => setInputValue(e.target.value)} className="flex-1" />
        <Button type="submit" size="icon" className="rounded-full bg-gold hover:bg-gold/90 text-black">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>;
};

export default AIConsultantChat;
